import {
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  FormControl,
  Typography,
  useMediaQuery,
  Skeleton,
  useTheme,
  Box
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { ErrorPage, mobileWidth, useToast } from "@qubit/autoparts";
import { skipToken } from "@reduxjs/toolkit/query";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { useLocation } from "react-router-dom";

import { PrinterConfiguration } from "~/api/warehouseTypes/fulfillmentCenter";
import { useAppDispatch, useAppSelector } from "~/app/store";
import useFlag from "~/config/flags";
import { CartSearchInput } from "~/features/cartSearchInput/CartSearchInput";
import PrintableToteLabels, {
  LabelInfo
} from "~/features/printableToteLabels/PrintableToteLabels";

import { useNavbar, ViewNameTranslation } from "~/hooks/useNavbar";

import {
  useMixpanelModalName,
  mixpanelTrack,
  mixpanelTrackModalDismiss
} from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { createNumberSequence } from "~/lib/shared";
import { temperatureZoneColors } from "~/lib/tempZone";

import { selectToteLabels } from "~/redux/selectors/batchSelectors";

import {
  useGetBatchQuery,
  usePostAssignCartMutation
} from "~/redux/warehouse/batches.hooks";

import { useGetTotesQuery } from "~/redux/warehouse/totes.hooks";

import { BatchCartPrepToolbar } from "./BatchCartPrepToolbar";
import { closeAssignCartModal } from "./batch.slice";

type Props = { viewTitle?: ViewNameTranslation };

const CustomerName = styled(Typography)(() => ({
  display: "flex",
  paddingTop: 10,
  lineHeight: "1.3em",
  height: 40,
  letterSpacing: ".03em",
  justifyContent: "center",
  alignItems: "center"
}));

const ToteId = styled(Typography)(() => ({
  color: "white",
  fontSize: 20,
  bottom: 4,
  position: "absolute",
  right: 0,
  left: 0
}));

export function ToteCard({ tote }: { tote: LabelInfo }) {
  const { palette } = useTheme();
  const { toteId, zone } = tote;
  const backgroundColor =
    temperatureZoneColors[
      zone.toLowerCase() as "ambient" | "chilled" | "frozen"
    ] || "primary.main";

  return (
    <Button key={toteId} sx={{ width: "100%", padding: 0 }}>
      <Card
        sx={{
          textAlign: "center",
          width: "100%",
          height: "120px",
          bgcolor: backgroundColor,
          border: `3px solid`,
          borderColor: backgroundColor
        }}
      >
        <>
          <Box sx={{ height: "100px", bgcolor: "background.default" }}>
            <CustomerName variant="overline">
              {`${tote.firstName} ${tote.lastName || ""}`}
            </CustomerName>
            <Typography
              sx={{ fontSize: "28px", color: "primary.main" }}
              display="block"
            >
              {tote.position}
            </Typography>
          </Box>
          <ToteId
            style={{ color: palette.primary.main }}
            display="block"
            gutterBottom
          />
        </>
      </Card>
    </Button>
  );
}

export function BatchCartPrep({ viewTitle }: Props) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { enableColorChanges } = useFlag();
  const { errorToast } = useToast();
  const { batchName = "" } = useParams<{ batchName: string }>();
  const location = useLocation();
  const trackedModalName = useMixpanelModalName("Scan Cart");

  const printComponentRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery(mobileWidth);

  const [postAssignCart] = usePostAssignCartMutation();

  useNavbar({
    centerComponent: useMemo(
      () => <BatchCartPrepToolbar printComponentRef={printComponentRef} />,
      [printComponentRef]
    ),
    viewTitle
  });

  const {
    data: batchResponse,
    isFetching: isFetchingBatch,
    error: batchError
  } = useGetBatchQuery(
    { batchId: batchName },
    { refetchOnMountOrArgChange: true }
  );

  const {
    data: totes,
    isFetching: isFetchingTotes,
    error: totesError
  } = useGetTotesQuery(
    batchResponse ? { batchId: batchResponse.batch.batchId } : skipToken,
    { refetchOnMountOrArgChange: true }
  );

  const batch = batchResponse?.batch;
  const printerConfiguration = useAppSelector(
    (state) =>
      (state.store.usersFulfillmentCenter
        ?.printerConfiguration as PrinterConfiguration) || null
  );

  const isAssignCartModalOpen = useAppSelector(
    (state) => state.batchSlice.isAssignCartModalOpen
  );

  const toteLabels = useAppSelector((state) =>
    selectToteLabels(state, batch, totes)
  );

  const [assignedCartModalOpen, setAssignedCartModalOpen] =
    useState<boolean>(false);

  // new cart number
  const [cartNumber, setCartNumber] = useState<string | null>(null);

  const toteCount: number = totes?.length || 0;
  const totesPerRow = toteCount > 6 ? 4 : 3;

  const closeModalReloadData = () => {
    // if the second modal is closes now (after successful assignment to the cart batch) remember the cart number
    setCartNumber(null);
    dispatch(closeAssignCartModal());
    setAssignedCartModalOpen(false);
  };

  const assignCart = async () => {
    // set cart for the batch
    if (isAssignCartModalOpen) {
      try {
        if (batch?.batchId != null && cartNumber) {
          await postAssignCart({
            batchId: batch.batchId,
            cartNumber
          }).unwrap();
          // if the cart is successfully assigned, close the first and open the second modal
          dispatch(closeAssignCartModal());
          setAssignedCartModalOpen(true);
        }
      } catch (err) {
        errorToast(getMessageFromRtkError(err));
      }
    } else {
      // close second modal after cart has been assigned to the batch
      closeModalReloadData();
    }
  };

  const confirmAssignCart = async () => {
    mixpanelTrack({
      trackedPageName: trackedModalName,
      type: "Button Click",
      label: "Confirm Assign Cart"
    });
    await assignCart();
  };

  const error = batchError || totesError;

  return error ? (
    <ErrorPage
      errorMessage={getMessageFromRtkError(error)}
      goBackLocation={location.pathname}
    />
  ) : (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `repeat(${totesPerRow}, 1fr)`,
          gap: 1,
          padding: isMobile ? "10px" : "20px"
        }}
      >
        {(isFetchingBatch || isFetchingTotes) &&
          createNumberSequence(6).map((num) => (
            <Card
              key={num}
              sx={{ textAlign: "center", width: "100%", height: "120px" }}
            >
              <Skeleton height={126} />
            </Card>
          ))}
        {!isFetchingBatch &&
          !isFetchingTotes &&
          toteLabels &&
          toteLabels.map((tote) => <ToteCard key={tote.toteId} tote={tote} />)}
      </Box>

      {/* Assign cart modal */}
      <Dialog
        key="assign-cart-dialog"
        open={!!(isAssignCartModalOpen || assignedCartModalOpen)}
        onClose={(_e, reason) => {
          mixpanelTrackModalDismiss(trackedModalName, reason);
          closeModalReloadData();
        }}
      >
        <DialogContent
          style={{
            textAlign: "center"
          }}
        >
          {isAssignCartModalOpen && (
            <DialogContentText style={{ color: "black" }}>
              {" "}
              {t("scan cart")}
            </DialogContentText>
          )}
          <FormControl style={{ margin: 10 }}>
            {isAssignCartModalOpen ? (
              <div style={{ width: "250px" }}>
                <CartSearchInput
                  setPreppedCartNumberCb={(cartNumber1) => {
                    setCartNumber(cartNumber1);
                  }}
                  placeholder={t("scan cart to assign")}
                  trackedPageName={trackedModalName}
                />
              </div>
            ) : (
              <>
                {assignedCartModalOpen && (
                  <Typography
                    component="p"
                    variant="subtitle1"
                    color="initial"
                    align="center"
                    style={{
                      paddingTop: "12%"
                    }}
                  >
                    <>
                      {t("cart")} {cartNumber} {t("assigned to batch")}{" "}
                      {batchName}
                    </>
                  </Typography>
                )}
              </>
            )}
          </FormControl>
        </DialogContent>
        <DialogActions>
          {isAssignCartModalOpen && (
            <Button
              autoFocus
              onClick={() => {
                mixpanelTrack({
                  trackedPageName: trackedModalName,
                  type: "Button Click",
                  label: "Cancel"
                });
                dispatch(closeAssignCartModal());
              }}
              color={enableColorChanges ? "secondary" : "primary"}
            >
              {t("cancel")}
            </Button>
          )}
          <Button
            disabled={
              isAssignCartModalOpen &&
              (cartNumber === null || Number.isNaN(cartNumber))
            }
            onClick={confirmAssignCart}
            color="primary"
            autoFocus
          >
            {t("confirm")}
          </Button>
        </DialogActions>
      </Dialog>
      {printerConfiguration === "PagePrint" && (
        <div style={{ display: "none" }}>
          {!!toteLabels?.length && (
            <PrintableToteLabels labels={toteLabels} ref={printComponentRef} />
          )}
        </div>
      )}
    </>
  );
}
